import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type WorkspaceMode = 'BID' | 'PROCURE';

interface WorkspaceContextType {
  mode: WorkspaceMode;
  setMode: (mode: WorkspaceMode) => void;
  toggleMode: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<WorkspaceMode>('BID');
  const location = useLocation();
  const navigate = useNavigate();

  // Sync mode based on URL path
  useEffect(() => {
    if (location.pathname.startsWith('/procure')) {
      setModeState('PROCURE');
    } else if (location.pathname.startsWith('/bid') || location.pathname === '/dashboard') {
      setModeState('BID');
    }
  }, [location.pathname]);

  const setMode = (newMode: WorkspaceMode) => {
    if (mode === newMode) {
      return;
    }
    setModeState(newMode);

    // Navigate to the default route for the selected workspace
    if (newMode === 'PROCURE') {
      navigate('/procure/dashboard');
    } else {
      navigate('/dashboard'); // Assuming /dashboard is the bid workspace default
    }
  };

  const toggleMode = () => {
    setMode(mode === 'BID' ? 'PROCURE' : 'BID');
  };

  return (
    <WorkspaceContext.Provider value={{ mode, setMode, toggleMode }}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};
